import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import type { SvgIconProps } from '@mui/material/SvgIcon'

import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import QuestionMark from '@mui/icons-material/QuestionMark'
import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined'
import CompareArrowsOutlined from '@mui/icons-material/CompareArrowsOutlined'
import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowForward from '@mui/icons-material/ArrowForward'
import TuneOutlined from '@mui/icons-material/TuneOutlined'
import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined'
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined'
import BookOutlined from '@mui/icons-material/BookOutlined'
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined'
import BackpackOutlined from '@mui/icons-material/BackpackOutlined'
import BadgeOutlined from '@mui/icons-material/BadgeOutlined'
import WarehouseOutlined from '@mui/icons-material/WarehouseOutlined'

import AppLink from './components/AppLink'
import { heapEvent } from '@equipmentshare/heaper'
import { Permission } from '../../graphql'
import { useFlags } from '@equipmentshare/feature-flags'

// Mini-variant drawer widths. Expanded is wide enough to seat the "New" badge
// beside the longest label ("Transfer Orders") without overlap, at the nav's
// designed body2 text size (sc-563618).
const EXPANDED_WIDTH = 240
const COLLAPSED_WIDTH = 63

export type LinkProps = {
	to: string,
	label: string,
	hidden: boolean,
	event: any,
	// Per-item rail icon.
	icon: React.ComponentType<SvgIconProps>,
	permissions?: Permission[],
	badge?: string,
}

// Routes, flags, and permission gates are unchanged from the prior nav.
export function getLinks( flags: any ): LinkProps[] {
	return [
		{
			to: '/',
			label: 'Inventory',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Inventory',
			icon: Inventory2Outlined,
		},
		{
			to: '/requests',
			label: 'Requests',
			hidden: !flags.costentoryEnableDemandRequests,
			event: 'Inventory - Navigation Menu - Click - Requests',
			icon: QuestionMark,
		},
		{
			to: '/reports/parts-on-work-orders',
			label: 'Work Order Items',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Parts on Work Orders',
			icon: ConstructionOutlined,
		},
		{
			to: '/reports/incoming-parts-transfers',
			label: 'Incoming Transfers',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Incoming Parts Transfers',
			icon: ArrowBack,
		},
		{
			to: '/reports/outgoing-parts-transfers',
			label: 'Outgoing Transfers',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Outgoing Parts Transfers',
			icon: ArrowForward,
		},
		{
			to: '/reports/inventory-adjustments',
			label: 'Adjustments',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Inventory Adjustments',
			icon: TuneOutlined,
		},
		{
			to: '/manage/item-masters',
			label: 'Item Master',
			hidden: !flags.costentoryEnableItemMaster,
			event: 'Inventory - Navigation Menu - Click - Item Masters',
			icon: AutoStoriesOutlined,
		},
		{
			to: '/manage/providers',
			label: 'Mfr Catalog',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Manufacturer Catalog',
			icon: MenuBookOutlined,
		},
		{
			to: '/manage/parts',
			label: 'Product Catalog',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Parts Catalog',
			icon: BookOutlined,
		},
		{
			to: '/manage/product-classes',
			label: 'Product Classes',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Product Classes',
			icon: AssignmentOutlined,
		},
		{
			to: '/manage/stores',
			label: 'Stores',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Stores',
			icon: BackpackOutlined,
		},
		{
			to: '/manage/permissions',
			label: 'Permissions',
			hidden: false,
			event: 'Inventory - Navigation Menu - Click - Permissions',
			icon: BadgeOutlined,
		},
		{
			to: '/transfer-orders',
			label: 'Transfer Orders',
			hidden: !flags.costentoryEnableTransferOrders,
			event: 'Inventory - Navigation Menu - Click - Transfer Orders',
			badge: 'New',
			icon: CompareArrowsOutlined,
		},
		{
			to: '/dc-admin',
			label: 'DC Admin',
			hidden: !flags.costentoryEnableDcAdmin,
			event: 'Inventory - Navigation Menu - Click - DC Admin',
			icon: WarehouseOutlined,
		},
	]
}

export type AppMenuProps = {
	// Expanded (icon + label) vs. collapsed icon-only rail; owned by the shell.
	open: boolean,
	onToggle: () => void,
}

const AppMenu: React.FunctionComponent<AppMenuProps> = ( { open, onToggle } ) => {
	const captureEvent = ( link: LinkProps ) => () => heapEvent( link.event, {} )
	const flags = useFlags()
	const links = getLinks( flags )

	return (
		<Drawer
			variant="permanent"
			open={open}
			sx={( theme ) => ( {
				'flexShrink': 0,
				'whiteSpace': 'nowrap',
				'boxSizing': 'border-box',
				'width': open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
				'transition': theme.transitions.create( 'width', {
					easing: theme.transitions.easing.sharp,
					duration: open
						? theme.transitions.duration.enteringScreen
						: theme.transitions.duration.leavingScreen,
				} ),
				'& .MuiDrawer-paper': {
					// In-flow within the shell flex row (not fixed under the header),
					// and a column so the collapse toggle can pin to the bottom.
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					overflowX: 'hidden',
					boxSizing: 'border-box',
					width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
					borderRight: '1px solid',
					borderColor: 'divider',
					transition: theme.transitions.create( 'width', {
						easing: theme.transitions.easing.sharp,
						duration: open
							? theme.transitions.duration.enteringScreen
							: theme.transitions.duration.leavingScreen,
					} ),
				},
			} )}
		>
			<List dense sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pt: 1 }}>
				{ links.map( ( link ) => (
					<AppLink key={link.to} link={link} onClick={captureEvent( link )} open={open} />
				) ) }
			</List>
			{/* Collapse toggle pinned to the bottom — not a header hamburger. */}
			<Box sx={{ p: 1 }}>
				{ open ? (
					<Button
						fullWidth
						variant="outlined"
						color="inherit"
						startIcon={<ChevronLeft />}
						onClick={onToggle}
						aria-expanded
					>
						Collapse sidebar
					</Button>
				) : (
					<Button
						variant="outlined"
						color="inherit"
						onClick={onToggle}
						aria-label="Expand navigation"
						aria-expanded={false}
						sx={{ minWidth: 0, width: '100%', px: 0 }}
					>
						<ChevronRight />
					</Button>
				) }
			</Box>
		</Drawer>
	)
}

export default AppMenu
